<template>
  <div class="users-page">
    <div class="page-header">
      <h1>Usuarios</h1>
      <div class="header-actions">
        <button class="logout-button" @click="handleLogout">Cerrar sesión</button>
      </div>
    </div>

    <div class="toolbar">
      <input
        v-model="search"
        type="text"
        class="search-input"
        placeholder="Buscar por nombre o email..."
        @input="onSearchInput"
      />
      <router-link to="/users/create" class="btn btn-primary">Agregar usuario</router-link>
    </div>

    <div v-if="loading" class="loading">
      <div class="spinner"></div>
      <p>Cargando usuarios...</p>
    </div>

    <div v-else-if="error" class="error-state">
      <p>{{ error }}</p>
      <button class="btn btn-secondary" @click="loadUsers">Reintentar</button>
    </div>

    <div v-else>
      <div v-if="users.length === 0" class="empty-state">
        <p v-if="search">No se encontraron usuarios para "{{ search }}"</p>
        <p v-else>No hay usuarios registrados</p>
      </div>

      <table v-else class="users-table">
        <thead>
          <tr>
            <th>Nombre</th>
            <th>Email</th>
            <th>Perfil</th>
            <th>Activo</th>
            <th>Acciones</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="user in users" :key="user.id">
            <td>{{ user.name }}</td>
            <td class="email-cell">{{ user.email }}</td>
            <td>{{ user.profile.name }}</td>
            <td>
              <label class="toggle">
                <input
                  type="checkbox"
                  :checked="user.active"
                  @change="toggleActive(user)"
                />
                <span class="toggle-slider"></span>
              </label>
            </td>
            <td class="actions-cell">
              <button class="btn btn-small" @click="openContracts(user)">Contratos</button>
              <button
                class="btn btn-small btn-password"
                @click="confirmChangePassword(user)"
                :disabled="changingPasswordFor === user.id"
              >
                {{ changingPasswordFor === user.id ? 'Cambiando...' : 'Cambiar clave' }}
              </button>
            </td>
          </tr>
        </tbody>
      </table>
    </div>

    <UserContractsDialog
      v-if="contractsDialog.open"
      :userId="contractsDialog.userId"
      :userName="contractsDialog.userName"
      @close="contractsDialog.open = false"
      @saved="onContractsSaved"
    />

    <!-- Confirm password change dialog -->
    <div v-if="showConfirm" class="modal-overlay" @click.self="showConfirm = false">
      <div class="modal-card">
        <h2>Cambiar contraseña</h2>
        <p>Se generará una nueva contraseña para:</p>
        <p class="modal-user-name">{{ selectedUser?.name }}</p>
        <p class="modal-user-email">{{ selectedUser?.email }}</p>
        <div class="modal-actions">
          <button class="btn btn-secondary" @click="showConfirm = false">Cancelar</button>
          <button class="btn btn-primary" @click="doChangePassword">Confirmar</button>
        </div>
      </div>
    </div>

    <!-- Password result dialog -->
    <div v-if="showResult" class="modal-overlay" @click.self="closeResult">
      <div class="modal-card">
        <h2>Nueva contraseña</h2>
        <p>La nueva contraseña para <strong>{{ selectedUser?.name }}</strong> es:</p>
        <div class="password-display" @click="copyPassword">
          <code>{{ generatedPassword }}</code>
          <span class="copy-hint">{{ copied ? 'Copiado' : 'Click para copiar' }}</span>
        </div>
        <div class="modal-actions">
          <button class="btn btn-primary" @click="closeResult">Cerrar</button>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, reactive, onMounted } from 'vue';
import { useRouter } from 'vue-router';
import { useAuthStore } from '../stores/auth';
import usersApi, { type User } from '../api/users';
import UserContractsDialog from '../components/UserContractsDialog.vue';

const router = useRouter();
const authStore = useAuthStore();

const users = ref<User[]>([]);
const loading = ref(true);
const error = ref('');
const search = ref('');

let searchTimeout: ReturnType<typeof setTimeout> | null = null;

const contractsDialog = reactive({
  open: false,
  userId: 0,
  userName: '',
});

// Password change state
const changingPasswordFor = ref<number | null>(null);
const showConfirm = ref(false);
const showResult = ref(false);
const selectedUser = ref<User | null>(null);
const generatedPassword = ref('');
const copied = ref(false);

const handleLogout = () => {
  authStore.logout();
  router.push('/login');
};

const loadUsers = async () => {
  loading.value = true;
  error.value = '';
  try {
    const response = await usersApi.getUsers(search.value || undefined);
    users.value = response.data.users;
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Error al cargar usuarios';
  } finally {
    loading.value = false;
  }
};

const onSearchInput = () => {
  if (searchTimeout) clearTimeout(searchTimeout);
  searchTimeout = setTimeout(loadUsers, 300);
};

const toggleActive = async (user: User) => {
  const newActive = !user.active;
  try {
    await usersApi.toggleUserActive(user.id, newActive);
    user.active = newActive;
  } catch (e) {
    const msg = e instanceof Error ? e.message : 'Error al cambiar estado';
    alert(msg);
  }
};

const openContracts = (user: User) => {
  contractsDialog.userId = user.id;
  contractsDialog.userName = user.name;
  contractsDialog.open = true;
};

const onContractsSaved = () => {
  contractsDialog.open = false;
};

const confirmChangePassword = (user: User) => {
  selectedUser.value = user;
  showConfirm.value = true;
};

const doChangePassword = async () => {
  if (!selectedUser.value) return;
  showConfirm.value = false;
  changingPasswordFor.value = selectedUser.value.id;
  try {
    const response = await usersApi.changePassword(selectedUser.value.id);
    generatedPassword.value = response.data.password;
    showResult.value = true;
  } catch (e) {
    error.value = e instanceof Error ? e.message : 'Error al cambiar contraseña';
  } finally {
    changingPasswordFor.value = null;
  }
};

const copyPassword = async () => {
  try {
    await navigator.clipboard.writeText(generatedPassword.value);
    copied.value = true;
    setTimeout(() => { copied.value = false; }, 2000);
  } catch {
    // Fallback: select text
  }
};

const closeResult = () => {
  showResult.value = false;
  generatedPassword.value = '';
  copied.value = false;
};

onMounted(loadUsers);
</script>

<style scoped>
.users-page {
  max-width: 1000px;
  margin: 0 auto;
  padding: 40px 20px;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 24px;
}

.page-header h1 {
  color: #e0e0e0;
  margin: 0;
}

.logout-button {
  background-color: #2a2a2a;
  border: 1px solid #3a3a3a;
  border-radius: 6px;
  color: #b0b0b0;
  cursor: pointer;
  font-size: 14px;
  padding: 8px 16px;
  transition: all 0.2s;
}

.logout-button:hover {
  background-color: #3a3a3a;
  color: #e0e0e0;
}

.toolbar {
  display: flex;
  gap: 12px;
  margin-bottom: 24px;
}

.search-input {
  flex: 1;
  padding: 10px 14px;
  background-color: #1a1a1a;
  border: 1px solid #3a3a3a;
  border-radius: 6px;
  color: #e0e0e0;
  font-size: 14px;
  outline: none;
  transition: border-color 0.2s;
}

.search-input::placeholder {
  color: #666;
}

.search-input:focus {
  border-color: #4a90d9;
}

.btn {
  padding: 8px 20px;
  border-radius: 6px;
  font-size: 14px;
  cursor: pointer;
  transition: all 0.2s;
  border: none;
  text-decoration: none;
  display: inline-flex;
  align-items: center;
}

.btn-primary {
  background-color: #4a90d9;
  color: white;
  white-space: nowrap;
}

.btn-primary:hover {
  background-color: #3a7bc8;
}

.btn-secondary {
  background-color: #2a2a2a;
  color: #b0b0b0;
  border: 1px solid #3a3a3a;
}

.btn-secondary:hover {
  background-color: #3a3a3a;
  color: #e0e0e0;
}

.btn-small {
  padding: 5px 12px;
  font-size: 13px;
  background-color: #2a2a2a;
  color: #b0b0b0;
  border: 1px solid #3a3a3a;
}

.btn-small:hover {
  background-color: #3a3a3a;
  color: #e0e0e0;
}

.btn-small:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.btn-password {
  color: #4a90d9;
}

.loading {
  text-align: center;
  padding: 60px 20px;
  color: #b0b0b0;
}

.error-state {
  text-align: center;
  padding: 40px 20px;
}

.error-state p {
  color: #ff6b6b;
  margin: 0 0 16px 0;
}

.empty-state {
  text-align: center;
  padding: 40px 20px;
  color: #666;
}

.users-table {
  width: 100%;
  border-collapse: collapse;
  background-color: #1a1a1a;
  border-radius: 8px;
  overflow: hidden;
}

.users-table th {
  text-align: left;
  padding: 12px 16px;
  color: #b0b0b0;
  font-size: 13px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  border-bottom: 1px solid #2a2a2a;
}

.users-table td {
  padding: 12px 16px;
  color: #e0e0e0;
  font-size: 14px;
  border-bottom: 1px solid #2a2a2a;
}

.users-table tr:last-child td {
  border-bottom: none;
}

.users-table tr:hover td {
  background-color: #222;
}

.email-cell {
  color: #b0b0b0 !important;
}

.actions-cell {
  display: flex;
  gap: 8px;
}

/* Toggle switch */
.toggle {
  position: relative;
  display: inline-block;
  width: 42px;
  height: 24px;
  cursor: pointer;
}

.toggle input {
  opacity: 0;
  width: 0;
  height: 0;
}

.toggle-slider {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #3a3a3a;
  border-radius: 24px;
  transition: background-color 0.2s;
}

.toggle-slider::before {
  content: '';
  position: absolute;
  height: 18px;
  width: 18px;
  left: 3px;
  bottom: 3px;
  background-color: #b0b0b0;
  border-radius: 50%;
  transition: transform 0.2s;
}

.toggle input:checked + .toggle-slider {
  background-color: #4a90d9;
}

.toggle input:checked + .toggle-slider::before {
  transform: translateX(18px);
  background-color: white;
}

.spinner {
  width: 30px;
  height: 30px;
  border: 3px solid #3a3a3a;
  border-top-color: #4a90d9;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 16px;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

/* Modal styles */
.modal-overlay {
  position: fixed;
  inset: 0;
  background-color: rgba(0, 0, 0, 0.6);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 100;
}

.modal-card {
  background-color: #1a1a1a;
  border-radius: 12px;
  padding: 30px;
  width: 100%;
  max-width: 420px;
  box-shadow: 0 8px 30px rgba(0, 0, 0, 0.4);
}

.modal-card h2 {
  color: #e0e0e0;
  margin: 0 0 16px 0;
  font-size: 20px;
}

.modal-card p {
  color: #b0b0b0;
  margin: 0 0 8px 0;
  font-size: 14px;
}

.modal-user-name {
  color: #e0e0e0 !important;
  font-weight: 600;
  font-size: 16px !important;
}

.modal-user-email {
  color: #888 !important;
  font-size: 13px !important;
  margin-bottom: 20px !important;
}

.modal-actions {
  display: flex;
  gap: 10px;
  justify-content: flex-end;
  margin-top: 24px;
}

.password-display {
  background-color: #0d0d0d;
  border: 1px solid #3a3a3a;
  border-radius: 6px;
  padding: 16px;
  margin-top: 16px;
  text-align: center;
  cursor: pointer;
  transition: border-color 0.2s;
}

.password-display:hover {
  border-color: #4a90d9;
}

.password-display code {
  color: #4caf50;
  font-size: 22px;
  font-weight: 600;
  letter-spacing: 1px;
  display: block;
  margin-bottom: 8px;
}

.copy-hint {
  color: #666;
  font-size: 12px;
}
</style>
